import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { config } from '../../appConfig'

import {
  Box,
  Chip,
  Grid,
  Paper,
  Typography
} from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import CurvatureGeometryScatterChart from '../components/CurvatureGeometryScatterChart';
import CurvatureGeometryMultiChart from '../components/CurvatureGeometryMultiChart';
import DataCard, { DataCardProps } from '../components/DataCard';
import DataHubTable from '../components/DataHubTable';
import MapComponent from '../components/Map'
import { queryCount, queryLayer, queryStatistics } from '../../api/arcgisApi';

type SeverityClass = 'severe' | 'high' | 'review';

// 2. Correct the severityClass mapping
const severityClass: Record<SeverityClass, 'default' | 'success' | 'warning' | 'info' | 'error'> = {
  severe: 'error',
  high: 'warning',
  review: 'info'
};
const columns: GridColDef[] = [
  {
    field: 'id',
    headerName: 'ID',
    flex: 0.2,
  },
  {
    field: 'Chainage Range',
    headerName: 'Chainage Range (km)',
    flex: 2,
    renderCell: (params) => (
      <span>
        Ch {params.row.from_ch_km} to {params.row.to_ch_km}
      </span>
    ),
  },
  {
    field: 'min_radius_m',
    headerName: 'Min Radius (m)',
    flex: 1.4,
    renderCell: (params) => {
      const value = params.row.min_radius_m;
      return value != null ? value.toFixed(1) : '';
    },
  },
  {
    field: 'severity_class',
    headerName: 'SEVERITY CLASS',
    flex: 2,
    renderCell: (params) => (
      <Chip
        label={params.value}
        color={severityClass[params.value.toLowerCase() as SeverityClass]}
        variant="outlined"
      />
    )
  },
];


// const rows = [
//   {
//     id: 1,
//     derivedInterval: 'Ch 17.45-17.77', rlpSlice: 'Road Widening, Ch 17.45-17.77', minR: 'Grades / DEM'
//   },
//   { id: 2, derivedInterval: 'Ch 7.09-7.28', rlpSlice: 'Road Widening, Ch 7.05-7.32', minR: '127.4 m (GIS)' },
//   { id: 3, derivedInterval: 'Ch 1.65-1.83', rlpSlice: 'Road Widening, Ch 1.60-1.88', minR: '121.4 m (GIS)' },
//   { id: 4, derivedInterval: 'Ch 53.83-54.05', rlpSlice: 'Road Widening, Ch 53.33-54.1', minR: '83.2.4 m (GIS)' },
//   { id: 5, derivedInterval: 'Ch 34.05-14.21', rlpSlice: 'Road Widening, Ch 33.05-15.21', minR: '165.4 m (GIS)' },
//   { id: 6, derivedInterval: 'Ch 51.83-53.81', rlpSlice: 'Road Widening, Ch 51.33-54.31', minR: '145.4 m (GIS)' },
//   { id: 7, derivedInterval: 'Ch 11.69-11.77', rlpSlice: 'Road Widening, Ch 11.19-12.27', minR: '149.4 m (GIS)' },
//   { id: 8, derivedInterval: 'Ch 1.55-1.61', rlpSlice: 'Road Widening, Ch 1.05-2.11', minR: '182.8.4 m (GIS)' },
//   { id: 9, derivedInterval: 'Ch 17.79-17.85', rlpSlice: 'Road Widening, Ch 16.79-18.85', minR: '126.8 m (GIS)' },
// ];
export default function CurvatureGeometryPage() {
  const [selectedPriority, setSelectedPriority] = useState<string[]>([]);
  const [chainageData, setChainageData] = useState<number[]>([])
  const [tightZoneData, setTightZoneData] = useState<number[]>([])
  const [sweepingZoneData, setSweepingZoneData] = useState<number[]>([])
  const [sweepingChartData, setSweepingChartData] = useState<number[]>([])
  const priorityOptions = useSelector((state: any) => state.global.priorityOptions);
  const [derivedMinRadius, setDerivedMinRadius] = useState<string>('0');
  const [fifthPercentile, setFifthPercentile] = useState<string>('0');
  const [derivedZoneLength, setDerivedZoneLength] = useState<string>('0');
  const [derivedMaxZoneLength, setDerivedMaxZoneLength] = useState<string>('0');
  const [zonesLessReviewCount, setZonesLessReviewCount] = useState<string>('0');
  const [zoneCount, setZoneCount] = useState<number>(0);
  const [chainageMax, setChainageMax] = useState<number>(0);
  const [chainageMin, setChainageMin] = useState<number>(0);
  const [curveWideningSectionCount, setCurveWideningSectionCount] = useState<number>(0);
  const [reAlightmentSectionCount, setReAlignmentSectionCount] = useState<number>(0);
  const [reProfilingSectionCount, setReProfilingSectionCount] = useState<number>(0);
  const [curveDensity, setCurveDensity] = useState<string>('');
  const cards: DataCardProps[] = [
    {
      title: 'CURVE WIDENING SECTIONS',
      value: curveWideningSectionCount.toString(),
      borderColor: 'rgb(0, 119, 255)',
    },
    {
      title: 'REALIGNMENT SECTIONS',
      value: reAlightmentSectionCount.toString(),
      borderColor: 'rgb(13, 197, 13)',
    },
    {
      title: 'RE-PROFILING SECTIONS',
      value: reProfilingSectionCount.toString(),
      borderColor: 'rgb(138, 43, 226)',
    },
  ]
  const mapDOMId = 'curvature-geometry-map'
  const cards2: DataCardProps[] = [
    {
      title: 'MIN DERIVED RADIUS',
      value: derivedMinRadius + " m",
      faq: 'Min derived radius is a point level dense circumradius (vertices can spike from digitising --- screening zones use sliding window medium vs review threshold - not this scalar minimum alone.',
      borderColor: 'rgb(0, 119, 255)',
    },
    {
      title: 'TIGHTEST 5% RADIUS',
      value: fifthPercentile + " m",
      borderColor: 'rgb(13, 197, 13)',
    },
    {
      title: 'TIGHT BEND ZONE LENGTH (KM)',
      value: derivedZoneLength + " km",
      description: 'From sections / capacity extents',
      borderColor: 'rgb(255, 165, 0)',
    },
    {
      title: 'ZONES < REVIEW RADIUS',
      value: zonesLessReviewCount,
      borderColor: 'rgb(165, 165, 255)',
      // description: 'From sections / capacity extents',
    },
    {
      title: 'CONTINUOUS TIGHT ZONE',
      value: derivedMaxZoneLength + " m",
      description: 'From sections / capacity extents',
      borderColor: 'rgb(226, 28, 111)',
    },
    {
      title: 'CURVE DENSITY (ZONES/10KM)',
      value: curveDensity,
      borderColor: 'rgb(28, 170, 226)',
    },
  ];

  // const surveyData = [
  //   { easting: 500, northing: 1000, elevation: 12.1 },
  //   { easting: 505, northing: 1005, elevation: 14.3 },
  //   { easting: 510, northing: 1010, elevation: 13.7 },
  //   { easting: 508, northing: 1007, elevation: 16.2 },
  //   { easting: 495, northing: 1002, elevation: 11.9 },
  //   { easting: 515, northing: 1013, elevation: 15.4 },
  //   { easting: 503, northing: 1008, elevation: 13.2 },
  //   { easting: 498, northing: 1004, elevation: 12.7 },
  //   { easting: 520, northing: 1016, elevation: 17.1 },
  //   { easting: 507, northing: 1009, elevation: 13.9 },
  //   { easting: 513, northing: 1012, elevation: 15.0 },
  //   { easting: 517, northing: 1014, elevation: 16.7 },
  //   { easting: 502, northing: 1003, elevation: 12.5 },
  //   { easting: 522, northing: 1019, elevation: 18.2 },
  //   { easting: 525, northing: 1020, elevation: 19.0 },
  //   { easting: 492, northing: 999, elevation: 10.8 },
  //   { easting: 528, northing: 1025, elevation: 20.1 },
  //   { easting: 532, northing: 1030, elevation: 21.3 },
  //   { easting: 536, northing: 1035, elevation: 21.8 },
  //   { easting: 540, northing: 1040, elevation: 22.4 },
  // ];

  // const priorityOptions = [
  //   { label: 'Route backbone', id: 1, color: 'grey' },
  //   { label: 'Chainage risks/labels', id: 2, color: 'black' },
  //   { label: 'RLP Geometry (all treatments)', id: 3, color: 'yellow' },
  //   { label: 'Curve widening', id: 4, color: 'violet' },
  //   { label: 'Realignment', id: 5, color: 'red' },
  //   { label: 'Reprofiling', id: 6, color: 'orange' },
  //   { label: 'Derived tight zones', id: 7, color: 'purple' },
  //   { label: 'Curvature samples / grade - coloured slots', id: 8, color: 'blue' },
  // ];
  // const [sweepingBendZonesTotalCount, setSweepingBendZonesTotalCount] = useState<number>(0);
  // const [fields, setFields] = useState<Record<string, any>[]>([]);
  const [attributes, setAttributes] = useState<Record<string, any>[]>([]);


  const handleViewReady = (event: Event) => {
    const view = ((event as any).target.view);

    async function fetchUpdate() {
      const chainageResult = await queryLayer(0, '1=1', 'https://aus.gis.arcadis.com/hosting/rest/services/Hosted/INLAND_FR_Chainage/FeatureServer')
      const chainageFeatures = chainageResult?.features?.map((item:any)=>{return item.attributes.ch_km})
      setChainageData(chainageFeatures)
      const sweepingResult = await queryLayer(2, '1=1', 'https://aus.gis.arcadis.com/hosting/rest/services/Hosted/INLAND_FR_Curvature_Data/FeatureServer')
      const sweepingFeatures = sweepingResult?.features?.map((item: any)=>( { from_ch_km: item.attributes.from_ch_km, to_ch_km: item.attributes.to_ch_km, min_radius_m: item.attributes.min_radius_m }))
      setSweepingZoneData(sweepingFeatures)
      const tightResult = await queryLayer(1, '1=1', 'https://aus.gis.arcadis.com/hosting/rest/services/Hosted/INLAND_FR_Curvature_Data/FeatureServer')
      setTightZoneData(tightResult?.features?.map((item: any)=>( { from_ch_km: item.attributes.from_ch_km, to_ch_km: item.attributes.to_ch_km, min_radius_m: item.attributes.min_radius_m })))
      const horizontalGeometryWorks = await queryLayer(5, '1=1', 'https://aus.gis.arcadis.com/hosting/rest/services/Hosted/Inland_Route_WFL1/FeatureServer')
      setCurveWideningSectionCount(horizontalGeometryWorks?.features?.filter((feature: any)=>feature?.attributes?.treatment == 'Curve widening')?.length || 0)
      setReAlignmentSectionCount(horizontalGeometryWorks?.features?.filter((feature: any)=>feature?.attributes?.treatment == 'Realignment')?.length || 0)
      setReProfilingSectionCount(horizontalGeometryWorks?.features?.filter((feature: any)=>feature?.attributes?.treatment == 'Re-profiling')?.length || 0)
    }
    if (view.ready) {
      fetchUpdate()
    }
  }

  const handleViewChange = (event: Event) => {

    const view = ((event as any).target.view);
    async function fetchUpdate() {
      const horizontalGeometryWorks = view?.layerViews?.items?.find((l: any) => l.layer.title === "27A Road Link Plan - Horizontal Geometry Works");
      const hGWFeatureSet = await horizontalGeometryWorks?.queryFeatures({
        geometry: view.extent
      });
      setCurveWideningSectionCount(hGWFeatureSet?.features?.filter((feature: any)=>feature?.attributes?.treatment == 'Curve widening')?.length || 0)
      setReAlignmentSectionCount(hGWFeatureSet?.features?.filter((feature: any)=>feature?.attributes?.treatment == 'Realignment')?.length || 0)
      setReProfilingSectionCount(hGWFeatureSet?.features?.filter((feature: any)=>feature?.attributes?.treatment == 'Re-profiling')?.length || 0)
    }
    if (view.ready) {
      fetchUpdate()
      const extraParams = {
        outFields: "objectid, from_ch_km, to_ch_km, severity_class, min_radius_m",
        returnGeometry: false,
        geometry: JSON.stringify(view.extent.toJSON())
      }
      const queryData = queryLayer(2, "1=1", config.arcgisCurvatureUrl, extraParams);
      queryData.then((response) => {
        setAttributes(response.features.map((f: any) => {
          const { objectid, ...rest } = f.attributes;
          return {
            id: objectid,
            ...rest
          };
        }))
      })

      const queryDataStatistics = queryStatistics(
        2,
        "min",
        "min_radius_m",
        "DerivedMin_min_radius_m",
        "1=1",
        config.arcgisCurvatureUrl,
        { returnGeometry: false, geometry: JSON.stringify(view.extent.toJSON()) }
      );
      queryDataStatistics.then((response) => {
        setDerivedMinRadius(response.toFixed(1))
      });

      const queryFifthPercentile = queryStatistics(
        2,
        "percentile_disc",
        "min_radius_m",
        "PERCE_min_radius_m",
        "1=1",
        config.arcgisCurvatureUrl,
        { returnGeometry: false, geometry: JSON.stringify(view.extent.toJSON()) },
        0.05
      );
      queryFifthPercentile.then((response) => {
        setFifthPercentile(response.toFixed(1))
      });

      const queryDataSum = queryStatistics(
        2,
        "sum",
        "zone_length_m",
        "DerivedMin_zone_length_m",
        "1=1",
        config.arcgisCurvatureUrl,
        { returnGeometry: false, geometry: JSON.stringify(view.extent.toJSON()) }
      );
      queryDataSum.then((response) => {
        const zoneLength = response / 1000
        setDerivedZoneLength(zoneLength.toFixed(2))
      })

      const queryZoneLessReview = queryLayer(
        2,
        "zone_median_radius_m_screening <= review_radius_m",
        config.arcgisCurvatureUrl,
        { geometry: JSON.stringify(view.extent.toJSON()), returnCountOnly: true }
      );
      queryZoneLessReview.then((response) => {
        setZonesLessReviewCount(response.count)
      })

      const queryMaxZoneLength = queryStatistics(
        2,
        "max",
        "zone_length_m",
        "DerivedMax_zone_length_m",
        "1=1",
        config.arcgisCurvatureUrl,
        { returnGeometry: false, geometry: JSON.stringify(view.extent.toJSON()) }
      );
      queryMaxZoneLength.then((response) => {
        setDerivedMaxZoneLength(response)
      })

      const queryDataCount = queryCount(
        2,
        "1=1",
        config.arcgisCurvatureUrl,
        {
          returnGeometry: false,
          geometry: JSON.stringify(view.extent.toJSON())
        }
      );
      queryDataCount.then((response) => {
        setZoneCount(response)
      })

      const extraParams2 = {
        returnGeometry: false,
        geometry: JSON.stringify(view.extent.toJSON())
      }

      const queryChainageDataMax = queryStatistics(
        0,
        "max",
        "ch_km",
        "DerivedMax_ch_km",
        "1=1",
        config.arcgisChainageUrl,
        extraParams2
      );
      queryChainageDataMax.then((response) => {
        setChainageMax(response)
      })

      const queryChainageDataMin = queryStatistics(
        0,
        "min",
        "ch_km",
        "DerivedMin_ch_km",
        "1=1",
        config.arcgisChainageUrl,
        extraParams2
      );
      queryChainageDataMin.then((response) => {
        setChainageMin(response)
      })
    }
  }
  useEffect(() => {
    const chainage_distance = chainageMax - chainageMin;
    if (chainage_distance === 0) return
    const metric: number = (zoneCount / (chainage_distance)) * 10;
    setCurveDensity(metric.toFixed(2))
  }, [chainageMax, chainageMin, zoneCount])
  return (
    <Box>
      {/* MAIN CONTENT */}
      <Grid container>
        {/* SIDEBAR */}
        <Grid size={{ xs: 12, md: 2.5, }}
          sx={{
            overflowY: 'auto',
            height: 'calc(100vh - 64px - 24px - 24px - 24.33px )' // screenHeight - navBarHeight - paddingTop - paddingBottom
          }}>
          <Paper style={{ padding: 10, overflowY: "auto", height: '100%' }}>
            <Typography sx={{ fontWeight: 700 }} variant='caption'>ROAD LINK PLAN - RLP GEOMETRY</Typography>
            <br />
            {cards.map((card) => (
              <DataCard
                key={card.title}
                title={card.title}
                value={card.value}
                faq={card.faq}
                sx={{
                  borderLeft: `3px solid ${card.borderColor}`,
                }}
              />
            ))}
            <br />
            <Typography sx={{ fontWeight: 700 }} variant='caption'>
              GIS-DERIVED SCREENING KPIs
            </Typography>

            {cards2.map((card) => (
              <DataCard
                key={card.title}
                title={card.title}
                value={card.value}
                description={card?.description}
                faq={card?.faq}
                // borderColor={card.borderColor}
                sx={{
                  borderLeft: `3px solid ${card.borderColor}`,
                }}
              />
            ))}
          </Paper>
        </Grid>
        {/* MAIN AREA */}
        <Grid size={{ xs: 12, md: 9.5 }}
          sx={{
            overflowY: 'auto',
            height: 'calc(100vh - 64px - 24px - 24px - 24.33px )' // screenHeight - navBarHeight - paddingTop - paddingBottom
          }}>
          <Paper elevation={2} sx={{ overflowY: "auto", height: "100%" }}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                height: "100%",
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  height: "100%",
                }}
              >
                {/* Example of MapComponent with a children node attached */}
                <MapComponent
                  referenceId={mapDOMId}
                  mapItem={config.curvatureMapItemId}
                  priorityFilter={selectedPriority}
                  onViewChange={handleViewChange}
                  onViewReady={handleViewReady}

                >
                  <arcgis-expand id="map-widget-firstposition" expanded={false} slot="bottom-left" expandTooltip="Legend">
                    <arcgis-legend />
                  </arcgis-expand>
                  <arcgis-expand expanded slot="top-left" expand-icon="table" expand-tooltip="Open Data Table">
                    <div style={{ width: '18vw', resize: 'both' }}>
                      <DataHubTable
                        rows={attributes}
                        columns={columns}
                        density='compact'
                        cellFontSize={'10px'}
                        headerFontSize={'10px'}
                      />
                    </div>
                  </arcgis-expand>
                  <arcgis-expand expand-icon="chart-gear" expand-tooltip="Open Curvature Geometry Multi Chart" slot="bottom-right">
                    <div className="map-widget-wrapper" style={{ width: '25vw', resize: 'both' }}>
                      <CurvatureGeometryMultiChart
                        minChainage={chainageMin}
                        maxChainage={chainageMax}
                        sweepingData={sweepingZoneData}
                        tightData={tightZoneData}
                      />
                    </div>
                  </arcgis-expand>
                </MapComponent>
              </Paper>
            </Paper>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  )
}